import { DonateFormSearchValues } from "@components/donate/DonateV3/useDonateFormSearchParams";
import { Big } from "big.js";
import { TypeOf } from "io-ts";
import { useCallback, useState } from "react";

import {
  CustomField,
  DonationFlowCustomizationResponse,
  RequirementLevel,
} from "@every.org/common/src/codecs/donationFlowCustomization";
import { Currency } from "@every.org/common/src/entity/types";
import { removeUndefinedOrNullValues } from "@every.org/common/src/helpers/objectUtilities";
import { getDonationFlowCustomizationRouteSpec } from "@every.org/common/src/routes/donationFlowCustomization";

import { useAsyncEffect } from "src/hooks/useAsyncEffect";
import { queryApi } from "src/utility/apiClient";

type GetDonationFlowCustomizationQueryParams = TypeOf<
  typeof getDonationFlowCustomizationRouteSpec.paramsCodec
>;
type GetDonationFlowCustomizationQueryTokens = TypeOf<
  typeof getDonationFlowCustomizationRouteSpec.tokensCodec
>;
type DonationFlowCustomizationApiResponse = TypeOf<
  typeof getDonationFlowCustomizationRouteSpec.responseBodyCodec
>;

export const SITE_DEFAULT_SUGGESTED_AMOUNTS = [40, 100, 250];
export const SITE_DEFAULT_DONOR_CONTACT_INFO: RequirementLevel = "optional";

export function hasDonationFlowCustomization(
  customization?: DonationFlowCustomizationResponse | null
) {
  return !!customization;
}

function normalizeHexColor(color?: string): string | undefined {
  if (!color) {
    return undefined;
  }

  return color.startsWith("#") ? color.slice(1) : color;
}

export function toCustomizationInitialValues({
  customization,
  currency,
}: {
  customization?: DonationFlowCustomizationResponse | null;
  currency: Currency;
}): DonateFormSearchValues {
  const customizationData = customization?.customization;

  if (!customizationData) {
    return {};
  }

  return {
    method: customizationData.paymentMethods?.join(","),
    amount: customizationData.fixedAmount,
    frequency: customizationData.frequency,
    minValue:
      customizationData.minAmount !== undefined
        ? {
            amount: new Big(customizationData.minAmount),
            currency,
          }
        : {
            amount: new Big(10),
            currency,
          },
    suggestedAmounts: customizationData.suggestedAmounts?.length
      ? customizationData.suggestedAmounts
      : SITE_DEFAULT_SUGGESTED_AMOUNTS,
    designation: customizationData.designation,
    themeColor: normalizeHexColor(customizationData.themeColor),
    address: customizationData.address,
    donorContactInfo:
      customizationData.donorContactInfo ?? SITE_DEFAULT_DONOR_CONTACT_INFO,
    customFields: customizationData.fields,
    customization: customization || undefined,
  };
}

export function mergeDonateFormInitialValues({
  urlValues,
  customization,
  currency,
}: {
  urlValues: DonateFormSearchValues;
  customization?: DonationFlowCustomizationResponse | null;
  currency: Currency;
}): DonateFormSearchValues {
  return {
    ...toCustomizationInitialValues({ customization, currency }),
    ...urlValues,
    customization: customization || undefined,
  };
}

export function useDonateFlowCustomization({
  nonprofitId,
  fundraiserId,
  customizationCode,
}: {
  nonprofitId: GetDonationFlowCustomizationQueryTokens["nonprofitId"];
  fundraiserId?: GetDonationFlowCustomizationQueryParams["fundraiserId"];
  customizationCode?: string;
}) {
  const [customization, setCustomization] =
    useState<DonationFlowCustomizationResponse>();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const asyncOperation =
    useCallback(async (): Promise<DonationFlowCustomizationApiResponse> => {
      setIsLoading(true);
      setHasError(false);

      const res = await queryApi(getDonationFlowCustomizationRouteSpec, {
        routeTokens: {
          nonprofitId,
        },
        queryParams: removeUndefinedOrNullValues({
          fundraiserId,
          code: customizationCode,
        }),
        body: {},
      });

      return res;
    }, [nonprofitId, fundraiserId, customizationCode]);

  useAsyncEffect({
    asyncOperation,
    handleResponse: useCallback(
      (response: DonationFlowCustomizationApiResponse) => {
        setCustomization(response || undefined);
        setIsLoading(false);
        setHasError(false);
      },
      []
    ),
    handleError: useCallback((_: Error) => {
      setCustomization(undefined);
      setIsLoading(false);
      setHasError(true);
    }, []),
  });

  return {
    customization,
    isLoading,
    hasError,
    addressRequirement: customization?.customization.address as
      | RequirementLevel
      | undefined,
    donorContactInfoRequirement: customization?.customization
      .donorContactInfo as RequirementLevel | undefined,
    customFields: customization?.customization.fields as
      | CustomField[]
      | undefined,
  };
}
